import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { useAppSelector } from "../app/store";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/atoms";
import { CaretDownIcon } from "../components/atoms/Icon/CommonIcons";
import { fetchOrganizations, type OrganizationResponse } from "../api/organizations";
import { fetchDepartments, type DepartmentResponse } from "../api/departments";
import { createStore, updateStore } from "../api/stores";
import {
    OrganizationSelectPopup,
    type OrganizationSelection,
} from "../components/molecules/OrganizationSelectPopup/OrganizationSelectPopup";
import {
    DEFAULT_STORE_STATUS,
    validateStoreCode,
    validateStoreName,
    validateStoreShortName,
    type StoreFormOutletContext,
    type StoreFormState,
} from "../components/StoreEditLayout";

/**
 * フォームフィールドラッパー
 */
const FormField = ({
    label,
    required = false,
    children,
}: {
    label: string;
    required?: boolean;
    children: React.ReactNode;
}) => (
    <div className="flex items-start gap-6">
        <label className="w-28 text-sm text-gray-700 pt-2 flex-shrink-0">
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <div className="flex-1">{children}</div>
    </div>
);

/**
 * 選択された組織レベルからパス文字列を組み立てる（例: 「東京エリア > 渋谷エリア」）
 */
const formatOrgPath = (selection: OrganizationSelection | null): string => {
    if (!selection) return "";
    return Object.keys(selection.orgLevels)
        .map(Number)
        .sort((a, b) => a - b)
        .map((level) => selection.orgLevels[level]?.name)
        .filter(Boolean)
        .join(" > ");
};

/**
 * 店舗登録・編集ページ（組織タブ）
 *
 * 所属組織は OrganizationSelectPopup（上位階層のみ選択）を使う。
 * 使用部門はバックエンド schema 確定までプレースホルダ。
 */
const StoreOrganizationFormPage = () => {
    const navigate = useNavigate();
    const {
        setPageError,
        setInvalidFields,
        pendingFormState,
        setPendingFormState,
    } = useOutletContext<StoreFormOutletContext>();
    const { id } = useParams<{ id?: string }>();
    const isEdit = !!id;

    const [saving, setSaving] = useState(false);
    const [isOrgPopupOpen, setIsOrgPopupOpen] = useState(false);
    const orgButtonRef = useRef<HTMLButtonElement>(null);

    // ホーム画面 / サイドメニューで選択中の第1階層を初期値として使う（「全〇〇」センチネルは除外）
    // 名前は localStorage の値ではなく DB の最新値で表示する（古い名前で表示されるのを防ぐ）
    const globalOrgLevel1 = useAppSelector((state) => state.storeSelection.orgLevels[1]);
    const { user } = useAuth();
    const [orgsById, setOrgsById] = useState<Map<number, OrganizationResponse> | null>(null);
    const [allOrgs, setAllOrgs] = useState<OrganizationResponse[] | null>(null);
    const [departmentOptions, setDepartmentOptions] = useState<DepartmentResponse[]>([]);
    const [departmentsLoading, setDepartmentsLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        fetchOrganizations()
            .then((res) => {
                if (cancelled) return;
                const map = new Map<number, OrganizationResponse>();
                for (const o of res.items) map.set(o.id, o);
                setOrgsById(map);
                setAllOrgs(res.items);
            })
            .catch((err) => console.error("Failed to fetch organizations:", err));
        fetchDepartments()
            .then((res) => {
                if (cancelled) return;
                setDepartmentOptions(res.items);
            })
            .catch((err) => console.error("Failed to fetch departments:", err))
            .finally(() => {
                if (!cancelled) setDepartmentsLoading(false);
            });
        return () => { cancelled = true; };
    }, []);

    const defaultOrganization = useMemo<OrganizationSelection | null>(() => {
        // 1. ホーム画面で第1階層 (organization.level=0) が選ばれていればそれを使う
        //    （DB から名前を再解決）
        if (globalOrgLevel1 && globalOrgLevel1.id !== 0) {
            const resolvedName = orgsById?.get(globalOrgLevel1.id)?.name ?? globalOrgLevel1.name;
            return {
                orgLevels: { 0: { id: globalOrgLevel1.id, name: resolvedName ?? "" } },
            };
        }
        // 2. ホームで未選択ならログイン中の企業（user.tenantId と一致する level=0 org）を使う
        if (allOrgs && user?.tenantId) {
            const rootOrg = allOrgs.find(
                (o) => o.level === 0 && o.tenant_id === user.tenantId,
            );
            if (rootOrg) {
                return {
                    orgLevels: { 0: { id: rootOrg.id, name: rootOrg.name ?? "" } },
                };
            }
        }
        return null;
    }, [globalOrgLevel1, orgsById, allOrgs, user?.tenantId]);

    // pendingFormState に未設定なら、グローバル選択を fallback として表示する
    const organization = pendingFormState?.organization ?? defaultOrganization;
    const orgPath = formatOrgPath(organization);

    // 基本情報の書式エラー有無（あれば「設定を完了」「登録する」を非活性にする）
    const hasBasicInfoFormatError =
        pendingFormState !== null &&
        (validateStoreCode(pendingFormState.code) !== null ||
            validateStoreName(pendingFormState.name) !== null ||
            validateStoreShortName(pendingFormState.shortName) !== null);

    const syncPending = useCallback(
        (next: { organization: OrganizationSelection | null; departmentIds: number[] }) => {
            setPendingFormState({
                code: pendingFormState?.code ?? "",
                name: pendingFormState?.name ?? "",
                shortName: pendingFormState?.shortName ?? "",
                status: pendingFormState?.status ?? DEFAULT_STORE_STATUS,
                organization: next.organization,
                departmentIds: next.departmentIds,
            });
        },
        [pendingFormState, setPendingFormState],
    );

    const validateRequired = (state: StoreFormState): boolean => {
        const missing: { key: string; label: string }[] = [];
        if (!state.code.trim()) missing.push({ key: "code", label: "店舗コード" });
        if (!state.name.trim()) missing.push({ key: "name", label: "店舗名" });
        if (missing.length > 0) {
            setInvalidFields(missing.map((f) => f.key));
            setPageError(`必須項目が入力されていません: ${missing.map((f) => f.label).join(", ")}`);
            return false;
        }
        return true;
    };

    const handleBack = () => {
        navigate(isEdit ? `/org-settings/store/${id}/edit` : "/org-settings/store/new");
    };

    const handleCancel = () => {
        navigate("/org-settings/store");
    };

    const handleComplete = async () => {
        const stateForValidation: StoreFormState = pendingFormState ?? {
            code: "",
            name: "",
            shortName: "",
            status: DEFAULT_STORE_STATUS,
            organization: null,
            departmentIds: [],
        };
        if (!validateRequired(stateForValidation)) return;

        setInvalidFields([]);
        setPageError(null);
        setSaving(true);
        try {
            if (isEdit && id) {
                // 編集: code / name / status / 略称 を更新する。
                // 所属組織・使用部門の更新はまだ未対応（次フェーズで実装）。
                // 編集: 所属組織は pendingFormState 優先 → なければ defaultOrganization fallback
                const effectiveOrg = stateForValidation.organization ?? defaultOrganization;
                const orgLevels = effectiveOrg?.orgLevels ?? {};
                const parentOrgId = Object.keys(orgLevels)
                    .map(Number)
                    .sort((a, b) => b - a)
                    .map((level) => orgLevels[level]?.id)
                    .find((id): id is number => id !== undefined && id !== null);
                await updateStore(parseInt(id, 10), {
                    code: stateForValidation.code,
                    name: stateForValidation.name,
                    status: stateForValidation.status,
                    short_name: stateForValidation.shortName || null,
                    parent_organization_id: parentOrgId,
                    department_ids: stateForValidation.departmentIds,
                });
                navigate("/org-settings/store");
            } else {
                // 新規: 所属組織は pendingFormState 優先 → なければ表示用の defaultOrganization fallback
                const effectiveOrg = stateForValidation.organization ?? defaultOrganization;
                const orgLevels = effectiveOrg?.orgLevels ?? {};
                const parentOrgId = Object.keys(orgLevels)
                    .map(Number)
                    .sort((a, b) => b - a)
                    .map((level) => orgLevels[level]?.id)
                    .find((id): id is number => id !== undefined && id !== null);
                if (parentOrgId === undefined) {
                    setPageError("所属組織を選択してください");
                    return;
                }
                await createStore({
                    code: stateForValidation.code,
                    name: stateForValidation.name,
                    status: stateForValidation.status,
                    short_name: stateForValidation.shortName || null,
                    parent_organization_id: parentOrgId,
                    department_ids: stateForValidation.departmentIds,
                });
                navigate("/org-settings/store");
            }
        } catch (err) {
            console.error("Failed to save store:", err);
            setPageError(err instanceof Error ? err.message : "保存に失敗しました");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex-1 flex flex-col bg-white rounded-lg overflow-hidden">
            <div className="flex-1 overflow-auto p-6">
                <div className="space-y-6">
                    {/* 所属組織（OrganizationSelectPopup: 上位階層のみ） */}
                    <FormField label="所属組織">
                        <button
                            ref={orgButtonRef}
                            type="button"
                            onClick={() => setIsOrgPopupOpen(!isOrgPopupOpen)}
                            className={clsx(
                                "w-80 text-left px-4 py-2 border border-gray-300 rounded-md text-sm flex items-center justify-between",
                                "bg-white text-gray-700 hover:bg-gray-50",
                            )}
                        >
                            <span className="truncate">
                                {orgPath || "選択してください"}
                            </span>
                            <CaretDownIcon className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                        </button>
                        <OrganizationSelectPopup
                            isOpen={isOrgPopupOpen}
                            onClose={() => setIsOrgPopupOpen(false)}
                            onSelect={(selection) => {
                                syncPending({
                                    organization: selection,
                                    departmentIds: pendingFormState?.departmentIds ?? [],
                                });
                                setIsOrgPopupOpen(false);
                            }}
                            currentSelection={organization ?? undefined}
                            anchorRef={orgButtonRef}
                            lockFirstLevel
                        />
                    </FormField>

                    {/* 使用部門 */}
                    <FormField label="使用部門">
                        {departmentsLoading ? (
                            <p className="pt-2 text-sm text-gray-500">読み込み中...</p>
                        ) : departmentOptions.length === 0 ? (
                            <p className="pt-2 text-sm text-gray-500">部門が登録されていません</p>
                        ) : (
                            <div className="grid grid-cols-[repeat(4,max-content)] gap-x-6 gap-y-2 pt-2">
                                {departmentOptions.map((dept) => {
                                    const checked = (pendingFormState?.departmentIds ?? []).includes(dept.id);
                                    return (
                                        <label key={dept.id} className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={(e) => {
                                                    const current = pendingFormState?.departmentIds ?? [];
                                                    const next = e.target.checked
                                                        ? [...current, dept.id]
                                                        : current.filter((id) => id !== dept.id);
                                                    syncPending({
                                                        organization: pendingFormState?.organization ?? null,
                                                        departmentIds: next,
                                                    });
                                                }}
                                            />
                                            <span className="text-sm text-gray-700">{dept.name}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                    </FormField>
                </div>
            </div>

            {/* フッターボタン */}
            <div className="flex justify-end gap-4 px-6 py-4">
                <Button variant="primary" size="md" onClick={handleBack} className="px-8">
                    1.基本情報に戻る
                </Button>
                <Button variant="outline" size="md" onClick={handleCancel} className="px-8">
                    キャンセル
                </Button>
                <Button
                    variant="primary"
                    size="md"
                    onClick={handleComplete}
                    disabled={saving || hasBasicInfoFormatError}
                    className="px-8"
                >
                    {saving ? (isEdit ? "更新中..." : "登録中...") : (isEdit ? "設定を完了する" : "登録する")}
                </Button>
            </div>
        </div>
    );
};

export default StoreOrganizationFormPage;
